import { createIntl, createIntlCache } from "@formatjs/intl";
import { Font, StyleSheet, Text, View } from "@react-pdf/renderer";
import PropTypes from "prop-types";
import React from "react";
import { noticeError } from "./App.newrelic";
import { dot } from "./FrenchReport/utils";
import notoBold from "./assets/NotoSans-Bold.ttf";
import notoRegular from "./assets/NotoSans-Regular.ttf";
import notoTCBold from "./assets/NotoSansTC-Bold.ttf";
import notoTCRegular from "./assets/NotoSansTC-Regular.ttf";

Font.register({
  family: "Noto Sans",
  fonts: [
    { src: notoRegular }, // font-style: normal, font-weight: normal
    { src: notoBold, fontWeight: 700 },
  ],
});

Font.register({
  family: "Noto Sans TC",
  fonts: [
    { src: notoTCRegular }, // font-style: normal, font-weight: normal
    { src: notoTCBold, fontWeight: 700 },
  ],
});

const cache = createIntlCache();
const intl = createIntl(
  {
    locale: "en-US",
    messages: {},
  },
  cache
);

export const safeString = (valueObject, propertyName) => {
  try {
    if (
      valueObject &&
      propertyName &&
      valueObject[propertyName] !== undefined &&
      valueObject[propertyName] !== null
    ) {
      return valueObject[propertyName].toString();
    } else {
      return " ";
    }
  } catch (e) {
    console.error(e);
    noticeError(e);
    return " ";
  }
};

export const safeZero = (valueObject, propertyName) => {
  try {
    if (
      valueObject &&
      propertyName &&
      valueObject[propertyName] !== undefined &&
      valueObject[propertyName] !== null
    ) {
      return valueObject[propertyName].toString();
    } else {
      return "0";
    }
  } catch (e) {
    console.error(e);
    noticeError(e);
    return "0";
  }
};

export const NumberAsPercent = ({
  percentValue,
  fractionDigits = 1,
  useDot,
}) => {
  const result = intl.formatNumber(
    typeof fractionDigits === "number"
      ? Number(percentValue).toFixed(fractionDigits)
      : percentValue ?? 0,
    {
      style: "decimal",
      maximumFractionDigits: 1,
      minimumFractionDigits: fractionDigits,
    }
  );
  return useDot ? dot(result) : result;
};

export const NumberAsTwoDigits = ({ value }) => {
  return intl.formatNumber(value ?? 0, {
    style: "decimal",
    minimumIntegerDigits: 2,
    maximumFractionDigits: 0,
  });
};

export const borderStyle = "1pt solid #000000";
export const borderStyleGray = "1pt solid #BCBCBC";
export const narrowCells = { width: "0.5cm" };
export const narrowCellsFrench = { width: "6mm" };
export const styles = StyleSheet.create({
  page: {
    padding: 20,
    flexDirection: "column",
    backgroundColor: "white",
    justifyContent: "flex-start",
  },
  section: {
    align: "flex-start",
    justifyContent: "flex-start",
    marginBottom: 10,
  },
  header: {
    fontFamily: ["Noto Sans", "Noto Sans TC"],
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  headerColumn: {
    flexDirection: "column",
    flex: 1,
  },
  tableHeaderGroup: {
    flexDirection: "column",
    margin: 0,
    padding: 0,
  },
  tableHeaderGroupRow: {
    flexDirection: "row",
    fontWeight: "bold",
    height: "4.4mm",
    margin: 0,
    padding: 0,
  },
  tableHeaderColumn: {
    height: "8.8mm",
    fontWeight: "bold",
  },
  tableHeaderColumnFrench: {
    height: "8.8mm",
    fontWeight: "normal",
  },
  tableHeaderCell: {
    height: "100%",
    fontFamily: ["Noto Sans", "Noto Sans TC"],
    fontSize: "7.8",
    fontWeight: "bold",
    margin: 0,
    padding: 0,
  },
  tableHeaderCellFrench: {
    height: "100%",
    fontSize: "6.8",
    fontWeight: "normal",
    margin: 0,
    padding: 0,
  },
  tableRow: {
    fontSize: "6",
    flexDirection: "row",
    borderRightWidth: 1,
    margin: 0,
    padding: 0,
  },
  tableRowFrench: {
    fontSize: "6",
    flexDirection: "row",
    margin: 0,
    padding: 0,
    height: "4mm",
  },
  tableCell: {
    overflow: "visible",
    textOverflow: "clip",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    height: "4.5mm",
    fontSize: "7pt",
    width: "8mm",
    margin: "0pt",
    padding: "0pt",
    textAlign: "center",
    justifyContent: "center",
    fontFamily: ["Noto Sans", "Noto Sans TC"],
  },
  cellText: {
    marginLeft: "0",
    width: "100%",
  },
});
export const pdfTableStyle = StyleSheet.create({
  table: {
    margin: 0,
    padding: 0,
    flexDirection: "column",
    backgroundColor: "white",
    justifyContent: "flex-start",
    borderWidth: 1,
    width: "100%",
    fontSize: "7",
  },
  row: {
    flexDirection: "row",
    border: borderStyle,
    borderBottomWidth: 1,
    height: "4.4mm",
    margin: 0,
    padding: 0,
  },
  column: {
    flexDirection: "column",
    border: borderStyle,
    borderRightWidth: 1,
    margin: 0,
    padding: 0,
  },
  cell: {
    fontFamily: ["Noto Sans", "Noto Sans TC"],
    borderRightWidth: 1,
    margin: 0,
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    width: "7mm",
  },
  text: {
    fontFamily: ["Noto Sans", "Noto Sans TC"],
    width: "100%",
  },
});

export const CellTextBoxDot = (props) => (
  <View style={{ ...styles.tableCell, ...props.style }}>
    <Text style={styles.cellText}>
      {props.noDot ? props?.children ?? " " : dot(props?.children ?? " ")}
    </Text>
  </View>
);

export const CellTextBoxNoBorder = (props) => (
  <View style={{ ...styles.tableCell, border: "none", ...props.style }}>
    <Text style={styles.cellText}>
      {props.noDot ? props?.children ?? " " : dot(props?.children ?? " ")}
    </Text>
  </View>
);

export const CellTextBox = (props) => (
  <View style={{ ...styles.tableCell, ...props.style }}>
    <Text style={styles.cellText}>{props?.children ?? " "}</Text>
  </View>
);

export const Table = (props) => {
  return (
    <View
      wrap={true}
      style={{
        ...pdfTableStyle.table,
        ...getElementStyles(props),
        borderWidth: props.borderless || props.nostyle ? 0 : 1,
        ...props.style,
      }}
    >
      {props.children}
    </View>
  );
};
Table.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  borderless: PropTypes.bool,
  fontSize: PropTypes.any,
};

const getElementStyles = ({ flex, width, height, fontSize }) => {
  return {
    ...(flex ? { flex: 1 } : {}),
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(fontSize ? { fontSize } : {}),
  };
};

export const Row = (props) => (
  <View
    break={props.break ? props.break : false}
    wrap={true}
    style={{
      ...(props.nostyle ?? false
        ? { flexDirection: "row" }
        : pdfTableStyle.row),
      ...getElementStyles(props),
      borderBottomWidth: props.borderless || props.nostyle ? 0 : 1,
      ...props.style,
    }}
  >
    {props.children}
  </View>
);
Row.propTypes = {
  nostyle: PropTypes.bool,
  flex: PropTypes.bool,
  fontSize: PropTypes.any,
  width: PropTypes.string,
  height: PropTypes.string,
  borderless: PropTypes.bool,
  break: PropTypes.bool,
};

export const Column = (props) => (
  <View
    style={{
      ...(props.nostyle ?? false ? {} : pdfTableStyle.column),
      ...{ borderRightWidth: props.borderless || props.nostyle ? 0 : 1 },
      ...getElementStyles(props),
      ...props.style,
    }}
  >
    {props.children}
  </View>
);
Column.propTypes = {
  nostyle: PropTypes.bool,
  borderless: PropTypes.bool,
  flex: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  fontSize: PropTypes.any,
};

export const Cell = (props) => {
  const borderStyle = {
    borderRightWidth: props.borderless || props.nostyle ? 0 : 1,
  };

  return (
    <View
      style={{
        ...(props.nostyle ?? false ? {} : pdfTableStyle.cell),
        ...getElementStyles(props),
        ...borderStyle,
        ...props.style,
      }}
    >
      {props.children}
    </View>
  );
};
Cell.propTypes = {
  borderless: PropTypes.bool,
  flex: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  fontSize: PropTypes.any,
  left: PropTypes.bool,
  right: PropTypes.bool,
};

export const TextCell = (props) => {
  const textStyle = props.left
    ? { textAlign: "left", paddingLeft: 2 }
    : props.right
    ? {
        textAlign: "right",
        paddingRight: 2,
      }
    : {};
  return (
    <Cell {...props}>
      <Text style={{ ...pdfTableStyle.text, ...textStyle }}>
        {props.children}
      </Text>
    </Cell>
  );
};
TextCell.propTypes = {
  borderless: PropTypes.bool,
  flex: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  fontSize: PropTypes.any,
  left: PropTypes.bool,
  right: PropTypes.bool,
};
